import React from 'react';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {createTheme} from '@mui/material/styles';
import {StylesProvider} from '@mui/styles';
import { GlobalStyles } from './global-styles';
import {inputOverrides} from './overrides';

const fontFamily = '"Open Sans", "Helvetica", "Arial", sans-serif';
// const fontSize = 14
// const htmlFontSize = 16
// const coef = fontSize / 14;


export const createBreakpointHelpers = (values, unit = 'px', step = 5) => {
  const keys = Object.keys(values); // ['xs','sm','md','lg','xl']

  const up = (key) => `@media (min-width:${values[key]}${unit})`;

  // MUI uses a tiny delta = step/100 to avoid overlap
  const down = (key) => {
    const end = values[key];
    const delta = step / 100;
    return `@media (max-width:${end - delta}${unit})`;
  };

  const between = (start, end) => {
    const delta = step / 100;
    return `@media (min-width:${values[start]}${unit}) and (max-width:${values[end] - delta}${unit})`;
  };

  const only = (key) => {
    const idx = keys.indexOf(key);
    const next = keys[idx + 1];
    return next ? between(key, next) : up(key);
  };

  const not = (key) => {
    const idx = keys.indexOf(key);
    const next = keys[idx + 1];
    if (next) {
      const delta = step / 100;
      return `@media not all and (min-width:${values[key]}${unit}) and (max-width:${values[next] - delta}${unit})`;
    }
    return `@media not all and (min-width:${values[key]}${unit})`;
  };

  return { keys, values, unit, step, up, down, between, only, not };
};


const breakpointValues = {
  xs: 0,
  sm: 600,
  md: 840,
  lg: 1280,
  xl: 1920,
};

const theme = createTheme({
  fontFamily: fontFamily,
  components: {
    MuiGrid: {
      defaultProps: {
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  },
  props: {
    MuiTypography: {
      variantMapping: {
        body1: 'div', // regular
        body2: 'div', // fine print
        subTitle: 'h6'
      }
    },
    desktopNavUpPx: 690,
    mobileNavDownPx: 689,
    desktopNavUp: '@media (min-width: 690px)',
    mobileNavDown: '@media (max-width: 689px)',
    desktopMediumUpPx: 1200,
    mobileMediumDownPx: 1199,
    // typography: {
    //   pxToRem: size => `${(size / htmlFontSize) * coef}rem`,
    // },
  },
  breakpoints: {
    values: breakpointValues
  }
});

// Ensure breakpoints object has all MUI v5 methods (up, down, between, only, not)
// This preserves the full breakpoints API for makeStyles contexts
const helpers = createBreakpointHelpers(breakpointValues);
theme.breakpoints = { ...theme.breakpoints, ...helpers };

// Optional convenience map for emotion/styled:
theme.media = theme.breakpoints.keys.reduce((acc, k) => {
  acc[k] = theme.breakpoints.up(k);
  return acc;
}, {});

// Add responsive utility functions to the theme
theme.isWidthUp = (breakpoint, width) => {
  // If width prop is provided (legacy usage from withWidth), use simple comparison
  if (width) {
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
    const currentIndex = breakpoints.indexOf(width);
    const targetIndex = breakpoints.indexOf(breakpoint);
    return currentIndex >= targetIndex;
  }
  
  // For browser environment, use window.matchMedia
  if (typeof window !== 'undefined') {
    const mediaQuery = theme.breakpoints.up(breakpoint);
    return window.matchMedia(mediaQuery.replace('@media ', '')).matches;
  }
  
  // Server-side rendering fallback
  return false;
};

theme.isWidthDown = (breakpoint, width) => {
  // If width prop is provided (legacy usage from withWidth), use simple comparison
  if (width) {
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
    const currentIndex = breakpoints.indexOf(width);
    const targetIndex = breakpoints.indexOf(breakpoint);
    return currentIndex <= targetIndex;
  }
  
  // For browser environment, use window.matchMedia
  if (typeof window !== 'undefined') {
    const mediaQuery = theme.breakpoints.down(breakpoint);
    return window.matchMedia(mediaQuery.replace('@media ', '')).matches;
  }
  
  // Server-side rendering fallback
  return false;
};

// Add utility functions to breakpoints for easy access
theme.breakpoints.isWidthUp = theme.isWidthUp;
theme.breakpoints.isWidthDown = theme.isWidthDown;

// Add media query helpers for makeStyles compatibility
// These return the actual CSS media query strings that makeStyles can use
theme.breakpoints.up = (breakpoint) => {
  // If breakpoint is a custom pixel value (string that can be parsed as number)
  if (!theme.breakpoints.values[breakpoint]) {
    const numericValue = parseInt(breakpoint, 10);
    if (!isNaN(numericValue)) {
      return `@media (min-width:${numericValue}px)`;
    }
    // If not a valid number, fall back to treating as 'xs'
    console.warn(`Invalid breakpoint "${breakpoint}" provided to theme.breakpoints.up(). Using 'xs' as fallback.`);
    return `@media (min-width:${theme.breakpoints.values.xs}px)`;
  }
  
  const value = theme.breakpoints.values[breakpoint];
  return `@media (min-width:${value}px)`;
};

theme.breakpoints.down = (breakpoint) => {
  const breakpointNames = ['xs', 'sm', 'md', 'lg', 'xl'];
  const breakpointIndex = breakpointNames.indexOf(breakpoint);
  
  // If breakpoint is a custom pixel value (string that can be parsed as number)
  if (breakpointIndex === -1) {
    const numericValue = parseInt(breakpoint, 10);
    if (!isNaN(numericValue)) {
      return `@media (max-width:${numericValue - 0.05}px)`;
    }
    // If not a valid number, fall back to treating as 'xs'
    console.warn(`Invalid breakpoint "${breakpoint}" provided to theme.breakpoints.down(). Using 'xs' as fallback.`);
    return `@media (max-width:${theme.breakpoints.values.xs - 0.05}px)`;
  }
  
  // For xl, there's no next breakpoint, so use xl value itself
  if (breakpointIndex === breakpointNames.length - 1) {
    const value = theme.breakpoints.values[breakpoint];
    return `@media (max-width:${value - 0.05}px)`;
  }
  
  // Use the next breakpoint value to include the current breakpoint range
  const nextBreakpoint = breakpointNames[breakpointIndex + 1];
  const nextValue = theme.breakpoints.values[nextBreakpoint];
  return `@media (max-width:${nextValue - 0.05}px)`;
};

theme.breakpoints.between = (start, end) => {
  const startValue = theme.breakpoints.values[start];
  const endValue = theme.breakpoints.values[end];
  return `@media (min-width:${startValue}px) and (max-width:${endValue - 0.05}px)`;
};

theme.breakpoints.only = (breakpoint) => {
  const breakpointNames = ['xs', 'sm', 'md', 'lg', 'xl'];
  const breakpointIndex = breakpointNames.indexOf(breakpoint);
  
  if (breakpointIndex === breakpointNames.length - 1) {
    // For 'xl', just use min-width
    return `@media (min-width:${theme.breakpoints.values[breakpoint]}px)`;
  }
  
  const nextBreakpoint = breakpointNames[breakpointIndex + 1];
  const startValue = theme.breakpoints.values[breakpoint];
  const endValue = theme.breakpoints.values[nextBreakpoint];
  return `@media (min-width:${startValue}px) and (max-width:${endValue - 0.05}px)`;
};


theme.palette = {
  ...theme.palette,
  black: '#000',
  white: '#fff',
  vividBlue: '#0170d4',
  vividBlueHover: '#015BAC',
  charcoal: '#2e3238',
  skyBlue: '#3fb8ff',
  skyBlueHover: '#0D7ABE',
  yellow: '#f8dd0a',
  gunmentalGrey: '#758094',
  shadeBlack: '#1E1E1E',
  darkGrey: '#7d7d7d',
  darkGrey2: '#282828',
  darkGrey3: '#414141',
  middleGrey: '#d6d6d6',
  lightGrey: '#f3f4f7',
  greyOpacity: 'rgba(46, 50, 56, 0.2)',
  greyLightOpacity: 'rgba(46, 50, 56, 0.8)',
  skyGradient: 'linear-gradient(270deg, #3FB8FF 0%, #0170D4 100%)',
  skyGradientLight: 'linear-gradient(270deg, rgb(63, 184, 255, 0.10) 0%, rgb(1, 112, 212, 0.10) 100%)',
  lightBlue: '#EBF7FF',
  background: {
    default: '#fff'
  },
  error: {
    ...theme.palette.error,
    form: '#BD10E0'
  }
};

theme.typography = {
    ...theme.typography,
    fontFamily: fontFamily,
    color: theme.palette.gunmentalGrey,
    h1: {
      ...theme.typography.h1,
      fontSize: '32px',
      fontFamily: fontFamily,
      //letterSpacing: '5px',
      fontWeight: 800,
      [theme.breakpoints.up('md')]: {
        fontSize: '42px',
        lineHeight: '1.06',
        fontWeight: 800,
        //letterSpacing: '8px'
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '60px',
        lineHeight: '1.06',
        fontWeight: 800,
        //letterSpacing: '10px'
      }
    },
    h2: {
      ...theme.typography.h2,
      fontSize: '18px',
      fontFamily: fontFamily,
      fontWeight: 800,
      //letterSpacing: '3px',
      lineHeight: '1.2',
      [theme.breakpoints.up('md')]: {
        fontSize: '23px',
        //letterSpacing: '6px'
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '44px',
        //letterSpacing: '8px'
      }
    },
    h3: {
      ...theme.typography.h3,
      fontSize: '18px',
      fontFamily: fontFamily,
      fontWeight: 700,
      marginTop: 0,
      [theme.breakpoints.up('md')]: {
        fontSize: '20px',
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '34px'
      }
    },
    h4: {
      ...theme.typography.h4,
      fontSize: '18px',
      fontFamily: fontFamily,
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        fontSize: '21px',
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '28px'
      }
    },
    h5: {
      ...theme.typography.h5,
      fontSize: '16px',
      fontFamily: fontFamily,
      fontWeight: 700,
      [theme.breakpoints.up('lg')]: {
        fontSize: '24px'
      }
    },
    h6: {
      ...theme.typography.h5,
      fontSize: '16px',
      fontFamily: fontFamily,
      fontWeight: 700,
      [theme.breakpoints.up('lg')]: {
        fontSize: '20px'
      }
    },
    body1: {
      ...theme.typography.body1,
      color: theme.palette.darkGrey,
      fontSize: '13px',
      fontFamily: fontFamily,
      [theme.breakpoints.up('lg')]: {
        fontSize: '16px'
      }
    },
    body2: {
      ...theme.typography.body2,
      color: theme.palette.darkGrey,
      fontSize: '10px',
      fontFamily: fontFamily,
      fontWeight: 400,
      [theme.breakpoints.up('lg')]: {
        fontSize: '12px'
      }
    },
    ul: {
      fontSize: '30px'
    }
};

theme.containers = {
  main: {
    width: '1352px',
    gutter: '16px',
  },
  full: {
    width: '1600px',
  }
};



// theme.overrides = {
//   ...theme.overrides,
//   ...inputOverrides(theme),
//   MuiGrid: {
//     root: {
//       '&.MuiGrid-container': {
//         maxWidth: '1400px',
//         margin: '0 auto',
//         width: '100%',
//       },
//       '&.hasPadding': {
//         maxWidth: '1432px',
//         overflow: 'hidden'
//       }
//     },
//     item: {
//       // paddingLeft: 16,
//       // paddingRight: 16,
//       // paddingTop: '0 !important',
//       // paddingBottom: '0 !important',
//     }
//   }
// };

theme.components = {
  ...theme.components,
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        lineHeight: '1.43',
        fontSize: '12px',
        letterSpacing: '0.01071em',
      },
    },
  },
  // ----- GRID -----
  MuiGrid: {
    defaultProps: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    styleOverrides: {
      root: {
        '&.MuiGrid-container': {
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
        },
        '&.hasPadding': {
          maxWidth: '1432px',
          overflow: 'hidden',
        }
      },
      item: {
        minWidth: '100%',
        // Example item-level overrides
        // paddingLeft: 16,
        // paddingRight: 16,
      },
    },
  },

  // ----- TYPOGRAPHY -----
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        body1: 'div',
        body2: 'div',
        subTitle: 'h6',
      },
    },
  },

  // Merge your custom input overrides
  ...inputOverrides(theme),
}

const DefaultTheme = props => {
  return (
    <StylesProvider injectFirst>
      <ThemeProvider theme={theme}>
        <GlobalStyles theme={theme}/>
        <CssBaseline />
        {props.children}
      </ThemeProvider>
    </StylesProvider>
  );
};

export {theme, DefaultTheme as default}
